export const SERVICE_PAGES = {
  digitalProductEngineering: {
    eyebrow: 'Digital Product Engineering',
    title: 'Design, build, and launch software products that customers can actually use.',
    summary: 'From discovery and UX to full-stack development and launch support, we help teams turn product ideas into reliable web platforms.',
    route: '/services/digital-product-engineering',
    accent: '#2563eb',
    timeline: '4-12 weeks',
    bestFor: 'Startups, SMEs, SaaS teams',
    outcomes: ['Validated product scope', 'Responsive product UI', 'Production-ready web app', 'Launch and iteration plan'],
    capabilities: [
      'Product strategy and discovery',
      'UX/UI design and design systems',
      'Full-stack web development',
      'API and third-party integrations',
      'Analytics, dashboards, and reporting',
      'Launch, QA, and post-launch support',
    ],
    process: [
      ['01', 'Clarify the product', 'We define users, workflows, business goals, risks, and launch scope.'],
      ['02', 'Design the experience', 'We map journeys, screens, states, and the interaction model before development.'],
      ['03', 'Build in releases', 'We ship usable increments with reviews, QA, and integration checks.'],
      ['04', 'Launch and improve', 'We deploy, monitor, collect feedback, and plan the next product iteration.'],
    ],
    stack: ['React', 'Next.js', 'Node.js', 'MongoDB', 'PostgreSQL', 'Stripe', 'AWS', 'Docker'],
  },
  legacySoftwareModernization: {
    eyebrow: 'Legacy Software Modernization',
    title: 'Modernize slow, fragile, outdated systems without disrupting your business.',
    summary: 'We refactor, rebuild, migrate, and stabilize legacy platforms so teams can move faster with lower technical risk.',
    route: '/services/legacy-software-modernization',
    accent: '#7c3aed',
    timeline: '3-16 weeks',
    bestFor: 'Existing products and internal tools',
    outcomes: ['Architecture audit', 'Modern UI and codebase', 'Improved performance', 'Safer deployment workflow'],
    capabilities: [
      'Legacy code audit',
      'Database and API migration',
      'UI rebuild and responsive redesign',
      'Performance optimization',
      'Security hardening',
      'Documentation and handover',
    ],
    process: [
      ['01', 'Audit the system', 'We inspect code, infrastructure, data flows, dependencies, and business-critical paths.'],
      ['02', 'Plan the migration', 'We separate quick wins from deeper rebuild work and define a low-risk rollout path.'],
      ['03', 'Modernize safely', 'We improve modules in stages while protecting existing operations.'],
      ['04', 'Stabilize and document', 'We test, deploy, document, and prepare the system for future changes.'],
    ],
    stack: ['React', 'Node.js', 'PHP', 'WordPress', 'MongoDB', 'MySQL', 'Docker', 'Cloudflare'],
  },
  mvpDevelopment: {
    eyebrow: 'MVP Development',
    title: 'Launch the smallest useful version of your product quickly and cleanly.',
    summary: 'We help founders and teams build focused MVPs that validate demand, reduce waste, and create a path to the next release.',
    route: '/services/mvp-development',
    accent: '#059669',
    timeline: '2-8 weeks',
    bestFor: 'Founders and early products',
    outcomes: ['Clear MVP scope', 'Clickable product experience', 'Core feature release', 'Feedback-ready launch'],
    capabilities: [
      'MVP scope planning',
      'Landing page and onboarding',
      'Core workflow development',
      'Authentication and payments',
      'Admin dashboard',
      'Analytics and launch support',
    ],
    process: [
      ['01', 'Define the bet', 'We isolate the highest-value problem your MVP needs to prove.'],
      ['02', 'Cut the scope', 'We remove nice-to-have features and keep the release focused.'],
      ['03', 'Build the core', 'We develop the main workflow, admin needs, and launch essentials.'],
      ['04', 'Measure feedback', 'We launch with analytics and prepare the next iteration roadmap.'],
    ],
    stack: ['React', 'Next.js', 'Node.js', 'Firebase', 'MongoDB', 'Stripe', 'Vercel', 'Tailwind CSS'],
  },
  saasDevelopment: {
    eyebrow: 'SaaS Development',
    title: 'Build subscription-ready SaaS platforms with dashboards, roles, billing, and scale.',
    summary: 'We design and develop SaaS products with tenant-aware architecture, secure user flows, subscription billing, and operational dashboards.',
    route: '/services/saas-development',
    accent: '#0891b2',
    timeline: '6-20 weeks',
    bestFor: 'Subscription products and platforms',
    outcomes: ['Tenant-ready architecture', 'Billing workflow', 'User and admin portals', 'Scalable product foundation'],
    capabilities: [
      'SaaS architecture planning',
      'User roles and permissions',
      'Subscription and billing integration',
      'Admin and analytics dashboard',
      'API development',
      'Cloud deployment and monitoring',
    ],
    process: [
      ['01', 'Map the product model', 'We define tenants, users, plans, permissions, and data ownership.'],
      ['02', 'Design core flows', 'We shape onboarding, billing, dashboard, and admin workflows.'],
      ['03', 'Build platform modules', 'We develop the app in stable, testable feature releases.'],
      ['04', 'Deploy and monitor', 'We launch with infrastructure, logging, and support readiness.'],
    ],
    stack: ['React', 'Node.js', 'MongoDB', 'PostgreSQL', 'Stripe', 'JWT', 'AWS', 'Chart.js'],
  },
  iotDevelopment: {
    eyebrow: 'IoT Development',
    title: 'Connect devices, data, dashboards, and alerts into practical IoT systems.',
    summary: 'We build IoT dashboards and data workflows for monitoring, device management, analytics, and real-time operational visibility.',
    route: '/services/iot-development',
    accent: '#0f766e',
    timeline: '6-18 weeks',
    bestFor: 'Connected operations and monitoring',
    outcomes: ['Device data pipeline', 'Real-time dashboard', 'Alerts and reporting', 'Operational visibility'],
    capabilities: [
      'IoT system architecture',
      'Sensor and device integrations',
      'Real-time dashboards',
      'Alert and notification workflows',
      'Data storage and analytics',
      'Cloud deployment',
    ],
    process: [
      ['01', 'Define the signal', 'We clarify devices, data frequency, events, and business decisions.'],
      ['02', 'Design the pipeline', 'We plan ingestion, storage, alerts, and dashboard requirements.'],
      ['03', 'Build visibility', 'We develop dashboards, controls, reports, and integration workflows.'],
      ['04', 'Test in operation', 'We validate reliability, alerts, and performance under realistic use.'],
    ],
    stack: ['Node.js', 'MQTT', 'React', 'MongoDB', 'InfluxDB', 'AWS IoT', 'WebSockets', 'Docker'],
  },
  cloudComputing: {
    eyebrow: 'Cloud Computing',
    title: 'Deploy faster, scale smarter, and keep your infrastructure under control.',
    summary: 'We design cloud infrastructure for web apps, SaaS products, APIs, and business platforms that need reliability and performance.',
    route: '/services/cloud-computing',
    accent: '#1d4ed8',
    timeline: '1-8 weeks',
    bestFor: 'Apps, APIs, SaaS, and hosting migrations',
    outcomes: ['Cloud architecture', 'Deployment pipeline', 'Monitoring setup', 'Performance and security baseline'],
    capabilities: [
      'Cloud architecture design',
      'Server and container deployment',
      'CI/CD pipeline setup',
      'CDN and WAF configuration',
      'Monitoring and alerting',
      'Performance optimization',
    ],
    process: [
      ['01', 'Assess the workload', 'We review traffic, app architecture, security needs, and scaling risks.'],
      ['02', 'Design the cloud setup', 'We pick infrastructure patterns that fit cost, reliability, and operations.'],
      ['03', 'Deploy and configure', 'We set up servers, containers, DNS, SSL, CDN, and deployment workflows.'],
      ['04', 'Monitor and improve', 'We add observability and tune the system after launch.'],
    ],
    stack: ['AWS', 'Google Cloud', 'Azure', 'Docker', 'Nginx', 'Cloudflare', 'Linux', 'GitHub Actions'],
  },
  supportMaintenance: {
    eyebrow: 'Support and Maintenance',
    title: 'Keep your website, app, or platform secure, updated, and improving.',
    summary: 'We provide ongoing support for bugs, updates, backups, monitoring, performance, security, and small feature improvements.',
    route: '/services/support-and-maintenance',
    accent: '#dc2626',
    timeline: 'Monthly or on-demand',
    bestFor: 'Live websites and active products',
    outcomes: ['Reduced downtime risk', 'Faster issue resolution', 'Regular updates', 'Clear maintenance workflow'],
    capabilities: [
      'Bug fixes and technical support',
      'Security updates',
      'Backup and restore checks',
      'Performance reviews',
      'Monitoring and alerting',
      'Small feature improvements',
    ],
    process: [
      ['01', 'Audit current state', 'We review the app, hosting, plugins, dependencies, backups, and known issues.'],
      ['02', 'Set support rules', 'We define severity, response flow, access, communication, and monthly priorities.'],
      ['03', 'Maintain and improve', 'We handle updates, fixes, optimizations, and recurring checks.'],
      ['04', 'Report clearly', 'We provide status, completed work, risks, and recommendations.'],
    ],
    stack: ['React', 'WordPress', 'Node.js', 'Linux', 'Cloudflare', 'MongoDB', 'MySQL', 'Monitoring'],
  },
};

export const ALL_SERVICE_CARDS = [
  SERVICE_PAGES.digitalProductEngineering,
  SERVICE_PAGES.legacySoftwareModernization,
  SERVICE_PAGES.mvpDevelopment,
  SERVICE_PAGES.saasDevelopment,
  SERVICE_PAGES.iotDevelopment,
  SERVICE_PAGES.cloudComputing,
  SERVICE_PAGES.supportMaintenance,
  {
    eyebrow: 'Managed Hosting',
    title: 'Managed hosting for websites, stores, SaaS apps, and business systems.',
    summary: 'Hosting, migration, SSL, backups, uptime checks, CDN, security, and support handled by a technical team.',
    route: '/hosting',
    accent: '#0f172a',
    timeline: 'Monthly',
    bestFor: 'Live websites and production apps',
    outcomes: ['Managed migration', 'SSL and CDN setup', 'Backups and monitoring', 'Support-ready hosting'],
  },
];
